//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, erc20WeiToNumber, otAssert } from '@shared/conversions'

import { ProviderReadOnly } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { Pool } from '@typechain-types/index'

export const redeemFromPoolTx =
  (props: { poolAddr: EthAddr; sharesInWei: ERC20Wei }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, sharesInWei } = props

    const activity: ActivityEnum = ActivityEnum.WITHDRAW

    try {
      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName, providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.LENDER)

      await redeemFromPool(webSocket, signer as Signer, providerReadOnly, poolAddr, sharesInWei, chainConfigurationName)

      dispatch(poolActions.redeemFromPoolSuccess(poolAddr))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 1005)
    }
  }

export const redeemFromPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  _providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  shares: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('redeemFromPool', poolAddr)
  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW, poolAddr, uuid, chainConfigurationName))

  const accountAddr: EthAddr = await lenderSigner.getAddress()
  const poolContract = new Contract(poolAddr, PoolAbi, lenderSigner) as unknown as Pool

  const maxRedeem = await poolContract.maxRedeem(accountAddr)

  const maxRedeemNumber = erc20WeiToNumber(maxRedeem.toString() as ERC20Wei)
  logOT.debug('maxRedeem', maxRedeem.toString(), 'trying to redeem', shares.toString(), accountAddr)

  let tx2
  if (erc20WeiToNumber(shares) > maxRedeemNumber) {
    logOT.debug('maxRedeem', maxRedeem.toString(), 'trying to redeem', shares.toString(), accountAddr)
    tx2 = await poolContract.redeem(maxRedeem, accountAddr, accountAddr)
  } else {
    logOT.debug('maxRedeem', maxRedeem.toString(), accountAddr)
    tx2 = await poolContract.redeem(shares, accountAddr, accountAddr)
    logOT.debug('redeemFromPool redeemTx')
  }

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
  )

  await tx2.wait()
}
